import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors';
import { useAttach } from '../useAttach';

import { flowsStore, type FlowsStoreState, type FlowProps } from './FlowsStore';
import { FlowUtils } from './FlowUtils';

export function useFlowsData() {
  useAttach(flowsStore, 'useFlowsData');
  return useStoreWithEqualityFn(flowsStore.store, selectData, shallow);
}
useFlowsData.store = flowsStore;

export function useFlowsById() {
  useAttach(flowsStore, 'useFlowsById');
  return useStoreWithEqualityFn(flowsStore.store, selectById, shallow);
}
useFlowsById.store = flowsStore;

export function useFlowsByFolderId() {
  useAttach(flowsStore, 'useFlowsByFolderId');

  const select = useCallback((state: FlowsStoreState) => {
    if (state.byId == null) {
      return { byFolderId: null };
    }

    const byFolderId: Record<string, Record<string, FlowProps>> = {};

    for (const flow of Object.values(state.byId)) {
      byFolderId[String(flow.folder)] = byFolderId[String(flow.folder)] ?? {};
      byFolderId[String(flow.folder)][flow.id] = flow;
    }

    return {
      byFolderId: byFolderId,
    };
  }, []);

  return useStoreWithEqualityFn(flowsStore.store, select, shallow);
}
useFlowsByFolderId.store = flowsStore;

export function useFlowsByCardOwnerUri() {
  useAttach(flowsStore, 'useFlowsByCardOwnerUri');

  const select = useCallback((state: FlowsStoreState) => {
    if (state.byId == null) {
      return { byCardOwnerUri: null };
    }

    const byCardOwnerUri: Record<string, Record<string, FlowProps>> = {};
    for (const flow of Object.values(state.byId)) {
      const cardOwnerUris = FlowUtils.getOwnerUrisSetFromCards(flow);

      for (const cardOwnerUri of cardOwnerUris.values()) {
        byCardOwnerUri[cardOwnerUri] = byCardOwnerUri[cardOwnerUri] ?? {};
        byCardOwnerUri[cardOwnerUri][flow.id] = flow;
      }
    }

    return {
      byCardOwnerUri: byCardOwnerUri,
    };
  }, []);

  return useStoreWithEqualityFn(flowsStore.store, select, shallow);
}
useFlowsByCardOwnerUri.store = flowsStore;

export function useFlow({ flowId }: { flowId: string }) {
  useAttach(flowsStore, 'useFlow');

  const select = useCallback(
    (state: FlowsStoreState) => {
      return {
        flow: state.byId?.[flowId] ?? null,
        flowInstance: state.data?.[flowId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [flowId],
  );

  return useStoreWithEqualityFn(flowsStore.store, select, shallow);
}
useFlow.store = flowsStore;

export function useFlowsAttach() {
  useAttach(flowsStore, 'useFlowsAttach');
}
useFlowsAttach.store = flowsStore;

export function useFlowsLoading() {
  useAttach(flowsStore, 'useFlowsLoading');
  return useStoreWithEqualityFn(flowsStore.store, selectIsLoading, Object.is);
}
useFlowsLoading.store = flowsStore;
